// Implements: F-UIX-009, F-UIX-005, F-UIX-007, F-UIX-016, F-UIX-018, F-UIX-019, F-UIX-020, F-ITK-007, F-IDN-012
/**
 * What every route inside the guard is drawn into (the redesign mockup's app shell, UX-03): a
 * sidebar, a 48px topbar and the page in a `<main>` landmark.
 *
 * The sidebar has three layouts, chosen by width as in the mockup:
 *
 *   - 1280px and wider: full (240px), folded to the 56px rail with `[` or the topbar button. The
 *     choice is remembered under `prototyper.panel.sidebar` (`panelMemory`, storage wrapped in
 *     try/catch: a browser that refuses storage simply forgets);
 *   - 1024 to 1279px: the rail, expanded on demand for this visit only;
 *   - below 1024px: no inline sidebar; the topbar button opens it as a modal drawer.
 *
 * The navigation is built from the server's permission answer (`navigation.ts`); the old header and
 * the sessions panel at the bottom are gone: the sessions live on `/cuenta/sesiones` (mockup v3 P1),
 * reached from the user menu with the other account tabs.
 */

import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router";

import { useCachedActiveLinkCount } from "../../modules/sharing/hooks/useShareLinks";
import { NO_FILTERS } from "../../modules/mockups/api/galleryFilters";
import { useDebouncedValue } from "../../modules/mockups/hooks/useDebouncedValue";
import { useMockups } from "../../modules/mockups/hooks/useMockups";
import { UsageMeterContainer } from "../../modules/backoffice/containers/UsageMeterContainer";
import { useIntakeEnabled } from "../../modules/intake/hooks/useWhatsAppLinking";
import { useSession } from "../../shared/api/session";
import { useTranslation } from "../../shared/i18n";
import type { t as translate } from "../../shared/i18n";
import { useTheme } from "../../shared/theme/ThemeProvider";
import { Icon } from "../../shared/ui/atoms/Icon";
import { Kbd } from "../../shared/ui/atoms/Kbd";
import { isEditableTarget } from "../../shared/ui/editableTarget";
import type { MenuEntry } from "../../shared/ui/molecules/Menu";
import { Drawer } from "../../shared/ui/organisms/Drawer";
import { readPanelOpen, writePanelOpen } from "../../shared/ui/panelMemory";
import { useKeyShortcut } from "../../shared/ui/useKeyShortcut";
import { useMediaQuery } from "../../shared/ui/useMediaQuery";
import type { ScopeEntry } from "../../types/api";
import styles from "./AppShell.module.css";
import { activeItemId, buildNavigation } from "./navigation";
import type { NavGroup } from "./navigation";
import { SidebarContent } from "./Sidebar";
import { ACCOUNT_TABS, accountHref, accountTabOf } from "./accountTabs";
import { CommandPalette, offeredActions } from "./CommandPalette";
import type { PaletteCommand } from "./CommandPalette";
import { ShortcutsDialog } from "./ShortcutsDialog";
import { OrgSwitcher, THEME_CYCLE, ThemeControl, UserMenu, useSignOut } from "./SidebarControls";
import { Topbar, TopbarSlotProvider } from "./Topbar";

export { THEME_CYCLE } from "./SidebarControls";

/** The `prototyper.panel.<id>` under which the folded sidebar is remembered. */
export const SIDEBAR_PANEL_ID = "sidebar";

/** The mockup's breakpoints, as media queries a `useMediaQuery` can answer. */
export const DRAWER_QUERY = "(max-width: 1023.98px)";
export const RAIL_QUERY = "(max-width: 1279.98px)";

const SIDEBAR_KEY = { key: "[" } as const;
const NO_SCOPES: readonly ScopeEntry[] = [];

/** My usage (the backoffice section every person has). */
const MY_USAGE_PATH = "/backoffice/usage";

/** The Lienzo routes draw edge to edge, full height: the editor is not a padded page column. */
function isCanvasRoute(pathname: string): boolean {
  return pathname === "/lienzo" || pathname.startsWith("/lienzo/");
}

const FocusMode = createContext<((on: boolean) => void) | null>(null);

/**
 * Focus mode (UX-05, Shift+\ in the editor): while a page passes `true` the sidebar steps aside;
 * it comes back when the page passes `false` or goes away. Outside a shell it does nothing.
 */
export function useShellFocusMode(on: boolean): void {
  const set = useContext(FocusMode);
  useEffect(() => {
    if (set === null) return undefined;
    set(on);
    return () => {
      set(false);
    };
  }, [set, on]);
}

export interface AppShellProps {
  readonly children: ReactNode;
  /** The pause, in milliseconds, before the palette asks the server for mockups (injected so no test sleeps). */
  readonly paletteSearchDelayMs?: number | undefined;
}

/** The palette asks for mockups only once at least this many characters are typed. */
const PALETTE_SEARCH_MIN = 2;
/** How many mockups the palette lists. */
const PALETTE_SEARCH_LIMIT = 6;

/** The breadcrumb of the current address: group and item, or a page outside the sidebar. */
function crumbsFor(groups: readonly NavGroup[], activeId: string | null, pathname: string, t: typeof translate): string[] {
  // The editor draws its own breadcrumb (the way back to the gallery and the editable name).
  if (pathname.startsWith("/lienzo/")) return [];
  for (const group of groups) {
    const item = group.items.find((candidate) => candidate.id === activeId);
    // A work page stands alone, as in the mockup; the other groups prefix their pages.
    if (item !== undefined) return group.id === "work" ? [t(item.labelKey)] : [t(group.labelKey), t(item.labelKey)];
  }
  const tab = accountTabOf(pathname);
  if (tab !== undefined) return [t("app.shell.account"), t(tab.labelKey)];
  if (pathname.startsWith("/backoffice/usage")) return [t("backoffice.nav.myUsage")];
  return [];
}

export function AppShell({ children, paletteSearchDelayMs = 250 }: AppShellProps): ReactElement {
  const { t } = useTranslation();
  const { state } = useSession();
  const user = state.status === "authenticated" ? state.view.user : null;
  const scopes = state.status === "authenticated" ? state.view.scopes : NO_SCOPES;
  // WhatsApp is offered only once the server answered the person's numbers (intake enabled).
  const intakeEnabled = useIntakeEnabled(user?.id ?? "", user !== null);
  const location = useLocation();
  const drawerMode = useMediaQuery(DRAWER_QUERY);
  const railByWidth = useMediaQuery(RAIL_QUERY);
  const [collapsed, setCollapsed] = useState(() => readPanelOpen(SIDEBAR_PANEL_ID) === false);
  const [expandedOnDemand, setExpandedOnDemand] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);

  // The active links the links page already loaded, if it loaded them all: never a request of its own.
  const activeLinks = useCachedActiveLinkCount();
  const groups = buildNavigation({ scopes, intakeEnabled });
  const activeId = activeItemId(groups, location.pathname);
  const rail = !drawerMode && (railByWidth ? !expandedOnDemand : collapsed);

  const toggle = useCallback((): void => {
    if (drawerMode) {
      setDrawerOpen(true);
    } else if (railByWidth) {
      setExpandedOnDemand((value) => !value);
    } else {
      // The memory is written here, once, never inside the updater (React may replay an updater).
      writePanelOpen(SIDEBAR_PANEL_ID, collapsed);
      setCollapsed(!collapsed);
    }
  }, [drawerMode, railByWidth, collapsed]);

  useKeyShortcut(SIDEBAR_KEY, true, toggle);

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [paletteQuery, setPaletteQuery] = useState("");
  // The mockups the typed text matches, asked of the server (`GET /mockups?q`) only once the text is long
  // enough; closing the palette clears the text, so nothing is asked for while it is closed.
  const searched = useDebouncedValue(paletteQuery.trim(), paletteSearchDelayMs);
  const searching = searched.length >= PALETTE_SEARCH_MIN;
  const found = useMockups({ ...NO_FILTERS, q: searched }, searching);
  const mockupCommands: PaletteCommand[] = !searching
    ? []
    : (found.data?.pages[0]?.items ?? []).slice(0, PALETTE_SEARCH_LIMIT).map((mockup) => ({
        id: `mockup-${mockup.id}`,
        groupLabel: t("app.palette.groupMockups"),
        label: mockup.name,
        icon: "canvas" as const,
        matched: true,
        run: () => {
          void navigate(`/lienzo/${encodeURIComponent(mockup.id)}`);
        },
      }));
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const signOut = useSignOut();

  // Ctrl+K (Cmd+K) opens the palette from anywhere, a field included, as in the mockup; `?` opens the
  // shortcuts sheet unless the reader is typing or a layer is open. `?` is matched by the character,
  // whatever modifier the keyboard layout needs to produce it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.repeat) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setPaletteOpen(true);
        return;
      }
      if (event.key !== "?" || event.ctrlKey || event.metaKey || event.altKey) return;
      if (isEditableTarget(event.target) || document.querySelector('[aria-modal="true"]') !== null) return;
      event.preventDefault();
      setShortcutsOpen(true);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const go = t("app.palette.groupGo");
  const preferences = t("app.palette.groupPreferences");
  const account = t("app.palette.groupAccount");
  const commands: PaletteCommand[] = [
    ...groups.flatMap((group) =>
      group.items.map((item) => ({
        id: `go-${item.id}`,
        groupLabel: go,
        label: t(item.labelKey),
        icon: item.icon,
        run: () => {
          void navigate(item.href);
        },
      })),
    ),
    {
      id: "new-mockup",
      groupLabel: t("app.palette.groupActions"),
      label: t("app.palette.newMockup"),
      icon: "plus",
      run: () => {
        void navigate("/lienzo");
      },
    },
    ...offeredActions(scopes).map((action) => ({
      id: action.id,
      groupLabel: t("app.palette.groupActions"),
      label: t(action.labelKey),
      icon: action.icon,
      run: () => {
        void navigate(action.href);
      },
    })),
    {
      id: "theme",
      groupLabel: preferences,
      label: t("app.palette.toggleTheme"),
      icon: "moon",
      run: () => {
        setTheme(THEME_CYCLE[theme]);
      },
    },
    { id: "sidebar", groupLabel: preferences, label: t("app.shortcuts.sidebar"), icon: "panel-left", shortcut: "[", run: toggle },
    {
      id: "shortcuts",
      groupLabel: preferences,
      label: t("app.shortcuts.title"),
      icon: "keyboard",
      shortcut: t("app.kbd.question"),
      run: () => {
        setShortcutsOpen(true);
      },
    },
    {
      id: "my-usage",
      groupLabel: account,
      label: t("backoffice.nav.myUsage"),
      icon: "chart",
      run: () => {
        void navigate(MY_USAGE_PATH);
      },
    },
    // Mockup v3 P1: the four tabs of the own account.
    ...ACCOUNT_TABS.map((tab) => ({
      id: `account-${tab.segment}`,
      groupLabel: account,
      label: t(tab.labelKey),
      icon: tab.icon,
      run: () => {
        void navigate(accountHref(tab));
      },
    })),
    { id: "sign-out", groupLabel: account, label: t("app.nav.signOut"), icon: "logout", run: signOut },
  ];

  // My usage is reachable whatever the meter shows (loading, failed, no row: verifier minor 1).
  const usageEntry: MenuEntry = {
    kind: "item",
    id: "my-usage",
    label: t("backoffice.nav.myUsage"),
    icon: "chart",
    onSelect: () => {
      void navigate(MY_USAGE_PATH);
    },
  };

  const shortcutsEntry: MenuEntry = {
    kind: "item",
    id: "shortcuts",
    label: t("app.shortcuts.title"),
    icon: "keyboard",
    shortcut: t("app.kbd.question"),
    onSelect: () => {
      setShortcutsOpen(true);
    },
  };

  const content = (inRail: boolean, touch: boolean, onNavigate?: () => void): ReactElement => (
    <SidebarContent
      groups={groups}
      activeId={activeId}
      rail={inRail}
      touch={touch}
      onNavigate={onNavigate}
      counts={activeLinks === null ? undefined : { sharing: { n: activeLinks, description: t("sharing.navActive", { n: activeLinks }) } }}
      switcher={<OrgSwitcher />}
      foot={
        <>
          <UsageMeterContainer rail={inRail} />
          <ThemeControl />
          {user === null ? null : <UserMenu user={user} extra={[usageEntry, shortcutsEntry]} />}
        </>
      }
    />
  );

  const layout = focusMode ? "focus" : drawerMode ? "drawer" : rail ? "rail" : "full";
  return (
    <FocusMode value={setFocusMode}>
      <TopbarSlotProvider>
        <div className={styles.shell} data-sidebar={layout}>
          {drawerMode ? (
            <Drawer
              open={drawerOpen}
              side="left"
              headless
              title={t("app.shell.sidebar")}
              onClose={() => {
                setDrawerOpen(false);
              }}
            >
              {content(false, true, () => {
                setDrawerOpen(false);
              })}
            </Drawer>
          ) : focusMode ? null : (
            <aside className={styles.sidebar} aria-label={t("app.shell.sidebar")}>
              {content(rail, false)}
            </aside>
          )}
          <div className={styles.main}>
            <Topbar
              mode={drawerMode ? "drawer" : "sidebar"}
              onToggle={toggle}
              crumbs={crumbsFor(groups, activeId, location.pathname, t)}
              trailing={
                <button
                  type="button"
                  className={styles.search}
                  aria-label={t("app.search.label")}
                  aria-keyshortcuts="Control+K"
                  onClick={() => {
                    setPaletteOpen(true);
                  }}
                >
                  <Icon name="search" />
                  <span className={styles.searchText}>{t("app.search.button")}</span>
                  <span className={styles.searchText}>
                    <Kbd>{t("app.kbd.ctrlK")}</Kbd>
                  </span>
                </button>
              }
            />
            <main className={styles.content} data-layout={isCanvasRoute(location.pathname) ? "canvas" : "page"}>
              {children}
            </main>
          </div>
        </div>
        <CommandPalette
          open={paletteOpen}
          commands={[...commands, ...mockupCommands]}
          onQuery={setPaletteQuery}
          onClose={() => {
            setPaletteOpen(false);
            setPaletteQuery("");
          }}
        />
        <ShortcutsDialog
          open={shortcutsOpen}
          onClose={() => {
            setShortcutsOpen(false);
          }}
        />
      </TopbarSlotProvider>
    </FocusMode>
  );
}
